export type FieldKind = "text" | "textarea" | "date" | "number" | "select" | "checkbox" | "rating" | "signature" | "media";

export type AuditField = {
  id: string;
  label: string;
  kind: FieldKind;
  required?: boolean;
  options?: string[];
  help?: string;
  normRef?: string;
  guidance?: string;
  sourceUrl?: string;
};

export type AuditSection = {
  id: string;
  title: string;
  source: string;
  fields: AuditField[];
};

export type AuditTemplate = {
  id: string;
  title: string;
  menuLabel?: string;
  standard: string[];
  domain: string;
  sections: AuditSection[];
};

export type NormReference = {
  id: string;
  category: "SAFETY" | "SECURITY" | "ENVIRONMENTAL" | "QUALITY" | "MEDICAL" | "PEOPLE" | "IT_CYBER" | "MARITIME";
  code: string;
  title: string;
  level: "International" | "EU" | "Italian" | "Standard";
  url: string;
  description: string;
};

const statusOptions = ["Conforme", "Osservazione", "Non conformita minore", "Non conformita maggiore", "Non applicabile"];
const yesNoNaOptions = ["Yes", "No", "N/A"];
const normUrls: Record<string, string> = {
  "D.Lgs. 81/2008": "https://www.normattiva.it/uri-res/N2Ls?urn:nir:stato:decreto.legislativo:2008;81~art306",
  "D.Lgs. 272/1999": "https://www.normattiva.it/uri-res/N2Ls?urn:nir:stato:decreto.legislativo:1999-07-27;272",
  "D.M. 1/09/2021": "https://www.gazzettaufficiale.it/eli/id/2021/09/25/21A05589/SG",
  "D.M. 2/09/2021": "https://www.gazzettaufficiale.it/eli/id/2021/10/04/21A05748/SG",
  "D.M. 3/09/2021": "https://www.gazzettaufficiale.it/eli/id/2021/10/29/21A06349/SG",
  "UNI EN ISO 7010": "https://www.iso.org/standard/72424.html",
  "ISO 45001:2018": "https://www.iso.org/standard/63787.html",
  "ISPS Code": "https://www.imo.org/en/ourwork/security/pages/solas-xi-2%20isps%20code.aspx",
  "SOLAS XI-2": "https://www.imo.org/en/ourwork/security/pages/solas-xi-2%20isps%20code.aspx",
  "Reg. (CE) 725/2004": "https://eur-lex.europa.eu/legal-content/IT/TXT/?uri=CELEX:32004R0725",
  "Dir. 2005/65/CE": "https://eur-lex.europa.eu/legal-content/IT/TXT/?uri=CELEX:32005L0065",
  "Reg. (CE) 324/2008": "https://eur-lex.europa.eu/legal-content/IT/TXT/?uri=CELEX:32008R0324",
  "D.M. 18/06/2004": "https://www.assocostieri.it/normativa/norm/09%20CIRCOLARI%202%C2%B0%20REPARTO%20-%20PORT%20SECURITY/DM%2018_06_2004%20competenze%20security%20CP.pdf",
  "D.Lgs. 203/2007": "https://www.normattiva.it/atto/caricaDettaglioAtto?atto.codiceRedazionale=007G0213&atto.dataPubblicazioneGazzetta=2007-11-09&bloccoAggiornamentoBreadCrumb=true&classica=true&dataVigenza=09%2F09%2F2025&generaTabId=true&tipoDettaglio=singolavigenza",
  "DM 415/27/03/2024": "https://www.gazzettaufficiale.it/eli/id/2024/04/05/24A01735/SG",
  "MIT Circ. 8/2023": "https://olympus.uniurb.it/index.php?option=com_content&view=article&id=29947%3Acp8_23&catid=6&Itemid=137",
  "MARPOL Annex I": "https://www.imo.org/en/OurWork/Environment/Pages/OilPollution-Default.aspx",
  "MARPOL Annex V": "https://www.imo.org/en/ourwork/environment/pages/garbage-default.aspx",
  "MARPOL Annex VI": "https://www.imo.org/en/OurWork/Environment/Pages/Air-Pollution.aspx",
  "EU 2019/883": "https://eur-lex.europa.eu/legal-content/IT/TXT/?uri=CELEX:32019L0883",
  "D.Lgs. 116/2020": "https://www.normattiva.it/uri-res/N2Ls?urn:nir:stato:decreto.legislativo:2020-09-03;116!vig=",
  "D.Lgs. 152/2006": "https://www.normattiva.it/uri-res/N2Ls?urn:nir:stato:decreto.legislativo:2006-04-03;152",
  "BWM Convention 2004": "https://www.imo.org/en/OurWork/Environment/Pages/BallastWaterManagement.aspx",
  "ISO 9001:2015": "https://www.iso.org/standard/62085.html",
  "ISO 14001:2015": "https://www.iso.org/standard/60857.html",
  "ISO 19011:2018": "https://www.iso.org/standard/70017.html",
  "ISO 31000:2018": "https://www.iso.org/standard/65694.html",
  "D.M. 388/2003": "https://www.normattiva.it/uri-res/N2Ls?urn:nir:ministero.:decreto:2003-07-15;388",
  "MFAG": "https://www.imo.org/en/ourwork/safety/pages/mfag.aspx",
  "MLC 2006": "https://www.ilo.org/global/standards/maritime-labour-convention/lang--en/index.htm",
  "STCW": "https://www.imo.org/en/OurWork/HumanElement/Pages/STCW-Convention.aspx",
  "Paris MoU 18201": "https://parismou.org/",
  "IMO MSC.428(98)": "https://www.imo.org/en/OurWork/Security/Pages/Cyber-security.aspx",
  "IACS UR E26": "https://iacs.org.uk/news/iacs-ur-e26-and-e27-press-release",
  "IACS UR E27": "https://iacs.org.uk/news/iacs-ur-e26-and-e27-press-release",
  "GDPR (EU) 2016/679": "https://eur-lex.europa.eu/eli/reg/2016/679/oj",
  "IEC 62443": "https://www.isa.org/standards-and-publications/isa-standards/isa-iec-62443-series-of-standards",
  "ISO 27001": "https://www.iso.org/isoiec-27001-information-security.html"
};

export const normativeReferences: NormReference[] = [
  { id: "safety-81-2008", category: "SAFETY", code: "D.Lgs. 81/2008", title: "Italian workplace health and safety consolidated act", level: "Italian", url: normUrls["D.Lgs. 81/2008"], description: "Core legal framework for workplace risk assessment, PPE, training, emergency management and employer duties." },
  { id: "safety-272-1999", category: "SAFETY", code: "D.Lgs. 272/1999", title: "Port operations safety rules", level: "Italian", url: normUrls["D.Lgs. 272/1999"], description: "Specific safety rules for port operations, cargo handling and port worker protection." },
  { id: "safety-fire-controls-2021", category: "SAFETY", code: "D.M. 1/09/2021", title: "Decreto Controlli - fire safety equipment maintenance and inspection", level: "Italian", url: normUrls["D.M. 1/09/2021"], description: "Current fire-safety reference for control, maintenance and inspection of fire protection systems, equipment and other fire-safety devices." },
  { id: "safety-fire-gsa-2021", category: "SAFETY", code: "D.M. 2/09/2021", title: "Decreto GSA - fire safety management in operation and emergency", level: "Italian", url: normUrls["D.M. 2/09/2021"], description: "Current fire-safety reference for emergency management, fire-safety organization, emergency plans, drills and training of fire wardens." },
  { id: "safety-fire-minicode-2021", category: "SAFETY", code: "D.M. 3/09/2021", title: "Decreto Minicodice - fire risk assessment criteria for low-risk workplaces", level: "Italian", url: normUrls["D.M. 3/09/2021"], description: "Current fire-safety reference for design, implementation and operation criteria in low fire-risk workplaces." },
  { id: "safety-7010", category: "SAFETY", code: "UNI EN ISO 7010", title: "Graphical safety signs", level: "Standard", url: normUrls["UNI EN ISO 7010"], description: "Standardized signs for safety, emergency, prohibition, warning and mandatory actions." },
  { id: "iso-45001", category: "SAFETY", code: "ISO 45001:2018", title: "Occupational health and safety management systems", level: "Standard", url: normUrls["ISO 45001:2018"], description: "OH&S management system requirements, hazard control, participation and continual improvement." },
  { id: "isps-code", category: "SECURITY", code: "ISPS Code", title: "International Ship and Port Facility Security Code", level: "International", url: normUrls["ISPS Code"], description: "PFSA, PFSP, PFSO, access control, restricted areas, security levels, drills and security duties." },
  { id: "solas-xi-2", category: "SECURITY", code: "SOLAS XI-2", title: "Special measures to enhance maritime security", level: "International", url: normUrls["SOLAS XI-2"], description: "SOLAS chapter introducing maritime security requirements for ships and port facilities." },
  { id: "eu-725-2004", category: "SECURITY", code: "Reg. (CE) 725/2004", title: "EU implementation of SOLAS XI-2 and ISPS Code", level: "EU", url: normUrls["Reg. (CE) 725/2004"], description: "EU security requirements for ships and port facilities, including mandatory application of relevant ISPS provisions." },
  { id: "eu-2005-65", category: "SECURITY", code: "Dir. 2005/65/CE", title: "Enhancing port security", level: "EU", url: normUrls["Dir. 2005/65/CE"], description: "Security requirements extended from port facilities to the wider port area." },
  { id: "eu-324-2008", category: "SECURITY", code: "Reg. (CE) 324/2008", title: "Commission procedures for maritime security inspections", level: "EU", url: normUrls["Reg. (CE) 324/2008"], description: "EU inspection procedures for monitoring maritime security compliance." },
  { id: "mit-2004", category: "SECURITY", code: "D.M. 18/06/2004", title: "Italian maritime security competent authority framework", level: "Italian", url: normUrls["D.M. 18/06/2004"], description: "First Italian implementation act for Reg. (CE) 725/2004: identifies the Corps of Port Captaincies as the competent maritime-security structure and assigns the General Command technical-operational functions and EU/IMO contact-point duties for ship and port-facility security." },
  { id: "dlgs-203-2007", category: "SECURITY", code: "D.Lgs. 203/2007", title: "Italian implementation of Directive 2005/65/CE on port security", level: "Italian", url: normUrls["D.Lgs. 203/2007"], description: "Completes and reinforces Reg. (CE) 725/2004 at port level by implementing Directive 2005/65/CE on security of ports, port security assessment and institutional coordination." },
  { id: "mit-415-2024", category: "SECURITY", code: "DM 415/27/03/2024", title: "Verification and certification of SOLAS XI-2/ISPS compliance", level: "Italian", url: normUrls["DM 415/27/03/2024"], description: "Italian maritime security verification and certification reference." },
  { id: "mit-circ-8-2023", category: "SECURITY", code: "MIT Circ. 8/2023", title: "Port Security inspection findings 2022 and corrective-action guidance", level: "Italian", url: normUrls["MIT Circ. 8/2023"], description: "Non-series Circular 8/2023 of the General Command of the Corps of Port Captaincies, issued on 2 May 2023, summarises 2022 port-facility security inspection findings and directs harmonised corrective action on recurring ISPS weaknesses: PFSO duties, PFSP procedural contents, security training/drills/exercises, access control, CCTV, fencing, anti-intrusion barriers, signage and lighting." },
  { id: "marpol-i", category: "ENVIRONMENTAL", code: "MARPOL Annex I", title: "Prevention of pollution by oil", level: "International", url: normUrls["MARPOL Annex I"], description: "Oil pollution prevention, oily residues, spill prevention and response controls." },
  { id: "marpol-v", category: "ENVIRONMENTAL", code: "MARPOL Annex V", title: "Prevention of pollution by garbage from ships", level: "International", url: normUrls["MARPOL Annex V"], description: "Garbage handling, placards, segregation, records and reception facility interface." },
  { id: "marpol-vi", category: "ENVIRONMENTAL", code: "MARPOL Annex VI", title: "Prevention of air pollution from ships", level: "International", url: normUrls["MARPOL Annex VI"], description: "Air emissions, fuel, BDN, SOx/NOx and related documentation controls." },
  { id: "eu-2019-883", category: "ENVIRONMENTAL", code: "EU 2019/883", title: "Port reception facilities for ship-generated waste", level: "EU", url: normUrls["EU 2019/883"], description: "EU framework for waste delivery, port reception facilities and reporting/traceability." },
  { id: "waste-116-2020", category: "ENVIRONMENTAL", code: "D.Lgs. 116/2020", title: "Waste package and circular economy implementation", level: "Italian", url: normUrls["D.Lgs. 116/2020"], description: "Waste classification, traceability and circular-economy implementation in Italy." },
  { id: "env-152-2006", category: "ENVIRONMENTAL", code: "D.Lgs. 152/2006", title: "Italian environmental consolidated act", level: "Italian", url: normUrls["D.Lgs. 152/2006"], description: "Core Italian environmental controls for waste, water, soil, emissions and pollution prevention." },
  { id: "bwm-2004", category: "ENVIRONMENTAL", code: "BWM Convention 2004", title: "Ballast Water Management Convention", level: "International", url: normUrls["BWM Convention 2004"], description: "Ballast water exchange/treatment and sediment management controls where relevant." },
  { id: "iso-9001", category: "QUALITY", code: "ISO 9001:2015", title: "Quality management systems", level: "Standard", url: normUrls["ISO 9001:2015"], description: "Quality management, processes, customers, documented information and improvement requirements." },
  { id: "iso-14001", category: "ENVIRONMENTAL", code: "ISO 14001:2015", title: "Environmental management systems", level: "Standard", url: normUrls["ISO 14001:2015"], description: "Environmental aspects, compliance obligations, operational control and improvement." },
  { id: "iso-19011", category: "QUALITY", code: "ISO 19011:2018", title: "Guidelines for auditing management systems", level: "Standard", url: normUrls["ISO 19011:2018"], description: "Audit principles, programme management, competence, risk-based planning, reporting and follow-up." },
  { id: "iso-31000", category: "QUALITY", code: "ISO 31000:2018", title: "Risk management guidelines", level: "Standard", url: normUrls["ISO 31000:2018"], description: "Risk framework and process supporting risk-based assessment and treatment." },
  { id: "medical-388-2003", category: "MEDICAL", code: "D.M. 388/2003", title: "First aid organization in workplaces", level: "Italian", url: normUrls["D.M. 388/2003"], description: "Italian workplace first-aid framework: classifies companies into groups A, B and C; defines minimum first-aid kit or packet requirements, communication tools for alerting the National Health Service, appointment and training of first-aid personnel, three-year practical refresher training and employer duties including ASL notification for Group A." },
  { id: "medical-mfag", category: "MEDICAL", code: "MFAG", title: "Medical First Aid Guide for accidents involving dangerous goods", level: "International", url: normUrls.MFAG, description: "IMO/WHO Medical First Aid Guide used as the chemical supplement to the International Medical Guide for Ships and reproduced in the IMDG Code Supplement. It supports emergency medical response for accidents involving dangerous goods and is commonly used with the EmS Guide for dangerous-goods incidents." },
  { id: "mlc-2006", category: "PEOPLE", code: "MLC 2006", title: "Maritime Labour Convention", level: "International", url: normUrls["MLC 2006"], description: "Labour, welfare, rest, medical care and employment condition reference for maritime contexts." },
  { id: "stcw", category: "PEOPLE", code: "STCW", title: "Standards of Training, Certification and Watchkeeping", level: "International", url: normUrls.STCW, description: "Competence, certification, familiarization and training requirements for maritime personnel." },
  { id: "paris-mou-18201", category: "PEOPLE", code: "Paris MoU 18201", title: "Fitness for duty - work and rest hours", level: "International", url: normUrls["Paris MoU 18201"], description: "PSC deficiency-code analogy for fatigue, work/rest records and fitness-for-duty controls." },
  { id: "cyber-msc-428", category: "IT_CYBER", code: "IMO MSC.428(98)", title: "Maritime cyber risk management in safety management systems", level: "International", url: normUrls["IMO MSC.428(98)"], description: "Cyber risk should be addressed in maritime safety management and operational resilience." },
  { id: "iacs-e26", category: "IT_CYBER", code: "IACS UR E26", title: "Cyber resilience of ships", level: "International", url: normUrls["IACS UR E26"], description: "Cyber resilience requirements useful as a benchmark for maritime operational technology." },
  { id: "iacs-e27", category: "IT_CYBER", code: "IACS UR E27", title: "Cyber resilience of onboard systems and equipment", level: "International", url: normUrls["IACS UR E27"], description: "System-level cyber resilience benchmark for connected maritime equipment." },
  { id: "gdpr", category: "IT_CYBER", code: "GDPR (EU) 2016/679", title: "Personal data protection", level: "EU", url: normUrls["GDPR (EU) 2016/679"], description: "Privacy, retention, access control and lawful handling of CCTV/personal data." },
  { id: "iec-62443", category: "IT_CYBER", code: "IEC 62443", title: "Industrial automation and control system security", level: "Standard", url: normUrls["IEC 62443"], description: "Security benchmark for OT/IACS segmentation, hardening and incident resilience." },
  { id: "iso-27001", category: "IT_CYBER", code: "ISO 27001", title: "Information security management systems", level: "Standard", url: normUrls["ISO 27001"], description: "Information security governance, risk assessment, controls and continual improvement." }
];

export function findNormReference(normRef?: string) {
  if (!normRef) return undefined;
  const normalized = normRef.toLowerCase();
  const compact = normalized.replace(/[^a-z0-9]/g, "");
  return normativeReferences.find((reference) => {
    const referenceCode = reference.code.toLowerCase();
    const referenceCompact = referenceCode.replace(/[^a-z0-9]/g, "");
    return normalized.includes(referenceCode) || referenceCode.includes(normalized) || compact.includes(referenceCompact) || referenceCompact.includes(compact);
  });
}

const evidenceFields: AuditField[] = [
  { id: "evidence_ref", label: "Riferimento evidenza oggettiva", kind: "text" },
  { id: "audit_note", label: "Nota auditor", kind: "textarea" },
  { id: "status", label: "Esito", kind: "select", options: statusOptions, required: true }
];

type ControlSpec = string | Pick<AuditField, "label" | "required" | "normRef" | "guidance" | "sourceUrl">;

function readControl(control: ControlSpec) {
  if (typeof control === "string") return { label: control };
  return control;
}

function withSource(control: ReturnType<typeof readControl>) {
  return {
    normRef: control.normRef,
    guidance: control.guidance,
    sourceUrl: control.sourceUrl || (control.normRef ? findNormReference(control.normRef)?.url : undefined)
  };
}

function controlFields(prefix: string, controls: ControlSpec[]): AuditField[] {
  return controls.flatMap((control, index) => [
    { id: `${prefix}_${index}_check`, label: `${readControl(control).label} - controllo effettuato?`, kind: "select", options: yesNoNaOptions, required: readControl(control).required ?? true, ...withSource(readControl(control)) },
    { id: `${prefix}_${index}_status`, label: `${readControl(control).label} - esito`, kind: "select", options: statusOptions, ...withSource(readControl(control)) },
    { id: `${prefix}_${index}_evidence`, label: `${readControl(control).label} - evidenze/documenti da verificare`, kind: "textarea", ...withSource(readControl(control)) },
    { id: `${prefix}_${index}_finding`, label: `${readControl(control).label} - rilievo, owner, azione richiesta`, kind: "textarea", ...withSource(readControl(control)) }
  ]);
}

function complianceFields(prefix: string, controls: ControlSpec[]): AuditField[] {
  return controls.map((control, index) => {
    const item = readControl(control);
    return {
      id: `${prefix}_${index}`,
      label: item.label,
      kind: "select",
      options: statusOptions,
      required: item.required,
      ...withSource(item)
    };
  });
}

export const templates: AuditTemplate[] = [
  {
    id: "iso-questionnaire",
    title: "ISO Questionnaire",
    menuLabel: "ISO Questionnaire",
    standard: ["ISO 9001:2015", "ISO 14001:2015", "ISO 45001:2018", "ISO 19011", "Port agency due diligence"],
    domain: "IMS, QHSE, process audit, agency context",
    sections: [
      {
        id: "audit-identity",
        title: "Identificazione audit e sito",
        source: "ISLA Internal Audit Report 02.2025 + Port Agent Info EUX2021.docx",
        fields: [
          { id: "report_date", label: "Report date", kind: "date", required: true },
          { id: "audit_period", label: "Audit between", kind: "text", required: true },
          { id: "place", label: "Place", kind: "textarea", required: true },
          { id: "auditor", label: "Auditor", kind: "textarea", required: true },
          { id: "previous_internal_audit", label: "Previous internal Audit", kind: "text" },
          { id: "audit_scope", label: "Audit scope", kind: "textarea", required: true },
          { id: "audit_method", label: "Audit method", kind: "textarea" },
          { id: "ims_scope", label: "Scope of IMS", kind: "textarea" },
          { id: "not_included", label: "Not included", kind: "textarea" },
          { id: "certificate_validity", label: "Certificate validity", kind: "text" },
          { id: "port_name", label: "Name of port", kind: "text" },
          { id: "local_agency", label: "Local Agency", kind: "text" },
          { id: "address", label: "Address - full and correct mailing address", kind: "textarea" },
          { id: "email", label: "E-Mail", kind: "text" },
          { id: "office_phone", label: "Office phone", kind: "text" },
          { id: "emergency_phone", label: "Emergency phone 24 hours", kind: "text" },
          { id: "website", label: "Website", kind: "text" },
          { id: "office_hours", label: "Office hours", kind: "text" }
        ]
      },
      {
        id: "audit-plan",
        title: "Piano, programma e responsabilita",
        source: "Auditprogram 2023-2025",
        fields: [
          { id: "management", label: "Management", kind: "select", options: statusOptions },
          { id: "rspp", label: "Responsible Person for Prevention and Protection / work safety", kind: "select", options: statusOptions },
          { id: "agency", label: "Agency", kind: "select", options: statusOptions },
          { id: "yachting_agency", label: "Yachting Agency", kind: "select", options: statusOptions },
          { id: "projects", label: "Projects", kind: "select", options: statusOptions },
          { id: "chartering", label: "Chartering", kind: "select", options: statusOptions },
          { id: "road_surveys", label: "Road Surveys", kind: "select", options: statusOptions },
          { id: "accounting_secretary", label: "Accounting / Secretary", kind: "select", options: statusOptions },
          { id: "effectiveness_check", label: "Checking of effectiveness during further internal audits", kind: "textarea" },
          { id: "person_title", label: "Person in charge Mr/Mrs", kind: "select", options: ["Mr", "Mrs", "Ms", "Other"] },
          { id: "person_name", label: "Person in charge - Name", kind: "text" },
          { id: "person_position", label: "Person in charge - Position", kind: "text" },
          { id: "person_email", label: "Person in charge - E-mail", kind: "text" },
          { id: "person_office_phone", label: "Person in charge - Office phone", kind: "text" },
          { id: "person_mobile_private", label: "Person in charge - Mobile and private phone", kind: "text" },
          { id: "health_authority_person", label: "Port Health Authority - Person in charge", kind: "text" },
          { id: "health_authority_email", label: "Port Health Authority - E-mail", kind: "text" },
          { id: "health_authority_phone", label: "Port Health Authority - Phone", kind: "text" }
        ]
      },
      {
        id: "chapter-4-6",
        title: "Context, Leadership, Planning",
        source: "ISO and Audit Reference Table.pdf",
        fields: [
          "4 Context of the organization",
          "4.1 Understanding the organization and its context",
          "4.2 Needs and expectations of workers and other interested parties",
          "4.3 Scope of integrated management system",
          "4.4 Integrated management system and processes",
          "5 Leadership and worker participation",
          "5.1 Leadership and commitment",
          "5.1.2 Customer focus",
          "5.2 Company policy",
          "5.3 Organizational roles, responsibilities and authorities",
          "5.4 Consultation and participation of workers",
          "6.1 Actions to address risks and opportunities",
          "6.1.2 Environmental aspects / OH&S hazard identification",
          "6.1.3 Compliance obligations and legal requirements",
          "6.2 Objectives and planning to achieve them",
          "6.3 Planning of changes"
        ].map((label, index) => ({ id: `iso_a_${index}`, label, kind: "select", options: statusOptions, normRef: label.startsWith("6.1") ? "ISO 31000:2018" : label.includes("Environmental") ? "ISO 14001:2015" : label.includes("worker") || label.includes("OH&S") ? "ISO 45001:2018" : "ISO 9001:2015", sourceUrl: normUrls["ISO 19011:2018"] }))
      },
      {
        id: "chapter-7-10",
        title: "Support, Operation, Performance, Improvement",
        source: "ISO and Audit Reference Table.pdf",
        fields: [
          "7.1 Resources",
          "7.1.2 People",
          "7.1.3 Infrastructure",
          "7.1.6 Organizational knowledge",
          "7.2 Competence",
          "7.3 Awareness",
          "7.4 Internal and external communication",
          "7.5 Documented information",
          "8.1 Operational planning and control",
          "8.1.2 Eliminating hazards and reducing OH&S risks",
          "8.1.3 Management of change",
          "8.1.4 Procurement",
          "8.2 Requirements for products and services",
          "8.4 Control of externally provided processes",
          "8.7 Control of nonconforming outputs",
          "9.1 Monitoring, measurement, analysis and evaluation",
          "9.2 Internal audit",
          "9.3 Management review",
          "10.2 Incident, nonconformity and corrective action",
          "10.3 Continual improvement"
        ].map((label, index) => ({ id: `iso_b_${index}`, label, kind: "select", options: statusOptions, normRef: label.includes("audit") ? "ISO 19011:2018" : label.includes("Environmental") ? "ISO 14001:2015" : label.includes("hazards") || label.includes("OH&S") || label.includes("Incident") ? "ISO 45001:2018" : "ISO 9001:2015", sourceUrl: normUrls["ISO 19011:2018"] }))
      },
      {
        id: "audit-result",
        title: "Esito, evidenza e follow-up",
        source: "ISLA Internal Audit Report 02.2025",
        fields: [
          ...evidenceFields,
          { id: "observations", label: "Observations", kind: "textarea" },
          { id: "remarks", label: "Remarks", kind: "textarea" },
          { id: "capa_owner", label: "CAPA owner", kind: "text" },
          { id: "capa_due_at", label: "CAPA due date", kind: "date" },
          { id: "follow_up_effectiveness", label: "Follow-up effectiveness", kind: "textarea" }
        ]
      }
    ]
  },
  {
    id: "q88-oil-chemical",
    title: "INTERTANKO Chartering Questionnaire 88 - Oil/Chemical",
    menuLabel: "Q88",
    standard: ["INTERTANKO Q88", "OCIMF", "MARPOL", "SOLAS"],
    domain: "Oil/Chemical vessel particulars",
    sections: [
      {
        id: "q88-general",
        title: "1. General Information, Ownership, Insurance, Classification, Dimensions",
        source: "MTMHamburg_Q88.pdf",
        fields: [
          "1.1 Date updated",
          "1.2 Vessel's name (IMO number)",
          "1.2b Owner/manager INTERTANKO membership and IMO number",
          "1.3 Vessel's previous names and dates of change",
          "1.4 Date delivered/Builder",
          "1.5 Flag/Port of Registry",
          "1.6 Call sign/MMSI",
          "1.7 Vessel's contact details",
          "1.8 Type of vessel",
          "1.8a Other vessel type",
          "1.9 Type of hull",
          "1.10 Registered owner - Full style",
          "1.11 Technical operator - Full style",
          "1.12 Commercial operator - Full style",
          "1.13 Disponent owner - Full style",
          "1.14 P&I Club - Full Style",
          "1.15 P&I pollution liability coverage/expiration date",
          "1.16 Hull & Machinery insured by - Full Style",
          "1.17 Hull & Machinery insured value/expiration date",
          "1.18 Classification society",
          "1.18a Classification Society IACS member",
          "1.19 Class notation",
          "1.20 Open conditions of Class",
          "1.20a Memoranda of Class",
          "1.21 Previous classification society and change date",
          "1.22 Ice class",
          "1.23 Date/place of last dry-dock",
          "1.24 Next dry dock due/next annual survey due",
          "1.25 Last special survey/next special survey due",
          "1.26 CAP overall rating",
          "1.27 Length overall",
          "1.28 Length between perpendiculars",
          "1.29 Extreme breadth",
          "1.30 Moulded depth",
          "1.31 Keel to masthead",
          "1.32 Distance bridge front to center of manifold",
          "1.33 Bow/Stern to center manifold",
          "1.34 Parallel body distances",
          "1.35 Net Tonnage",
          "1.36 Gross Tonnage/Reduced Gross Tonnage",
          "1.37 Suez Canal Tonnage Gross/Net",
          "1.38 Panama Canal fit/PCNT",
          "1.39 Loadline",
          "1.40 FWA/TPC at summer draft",
          "1.41 Multiple deadweights",
          "1.42 Constant excluding fresh water",
          "1.43 UKC company guidelines",
          "1.44 Air draft"
        ].map((label, index) => ({ id: `q88_general_${index}`, label, kind: "textarea" }))
      },
      {
        id: "q88-cert-crew-response",
        title: "2-5. Certificates, Crew, Response, Management System",
        source: "MTMHamburg_Q88.pdf",
        fields: [
          "2.1 Safety Equipment Certificate",
          "2.2 Safety Radio Certificate",
          "2.3 Safety Construction Certificate",
          "2.4 International Loadline Certificate",
          "2.5 IOPP Certificate",
          "2.6 International Ship Security Certificate",
          "2.7 Maritime Labour Certificate",
          "2.8 Minimum Safe Manning Certificate",
          "2.9 ISM Safety Management Certificate",
          "2.10 Document of Compliance",
          "2.11 USCG Certificate of Compliance",
          "2.12 CLC 1992 Certificate",
          "2.13 CLBC Certificate",
          "2.14 Wreck Removal Certificate",
          "2.15 U.S. COFR",
          "2.16 Certificate of Class",
          "2.17 Certificate of Registry",
          "2.18 Sewage Pollution Prevention Certificate",
          "2.19 Certificate of Fitness Chemical",
          "2.20 Certificate of Fitness Gas",
          "2.21 IEEC",
          "2.22 IAPPC",
          "2.23 Ship Sanitation Certificate",
          "2.24 Ballast Water Management Certificate",
          "2.25 ITOPF membership warranty",
          "2.26 Drug and Alcohol Policy",
          "2.27 ITF Special Agreement",
          "2.28 ITF Blue Card expiry",
          "3.1 Nationality of Master",
          "3.2 Number and nationality of Officers",
          "3.3 Number and nationality of Crew",
          "3.4 Common working language",
          "3.5 Officers speak and understand English",
          "3.6 Manning agency full style",
          "4.1 Vessel Spill Response Plan to USCG",
          "4.2 Qualified Individual full style",
          "4.3 OSRO full style",
          "4.4 SMFF full style",
          "5.1 Quality Management System",
          "5.2 ICS Helicopter Guidelines compliance",
          "5.2.1 Winching or landing area",
          "5.2.2 Diameter of provided circle"
        ].map((label, index) => ({ id: `q88_cert_${index}`, label, kind: "textarea" }))
      },
      {
        id: "q88-cargo-mooring-engine-sts",
        title: "6-12. Cargo, Ballast, Mooring, Engine, STS, History",
        source: "MTMHamburg_Q88.pdf",
        fields: [
          "6.1 Cargo tanks",
          "7.1 Ballast Handling Data",
          "7.2 D1/D2 performance standards",
          "7.3 BWTS fitted",
          "7.4 BWTS type",
          "7.5 BWTS manufacturer",
          "7.6 IMO type approval",
          "7.7 USCG approved type",
          "8.1 Centerline bulkhead",
          "8.2 Cargo Tank Capacities 98%",
          "8.2a Grand Total Cubic Capacity 98%",
          "8.2.1 Natural segregations with double valve",
          "8.2.2 IMO class",
          "8.3 Slops tank capacities",
          "8.3.1 Slops segregations and double valve",
          "8.3.2 Residual/retention oil tanks",
          "8.4 Grades/products with double valve segregation",
          "8.4.1 Cargo containment type",
          "8.5 Cargo tank filling restrictions",
          "8.6 Maximum loading rate",
          "8.7 Cargo Control Room",
          "8.8 CCR innage/ullage",
          "8.9 Gauging system certification/calibration",
          "8.9.1 Multipoint gauging",
          "8.10 Portable gauging units",
          "8.11 Vapour return system",
          "8.12 VECS manifolds",
          "8.13 VECS reducers",
          "8.14 Venting system",
          "8.15 Cargo manifold connections",
          "8.15.1 Fixed common line",
          "8.16 Manifold valves",
          "8.17 Manifold material/rating",
          "8.17.1 OCIMF manifold compliance",
          "8.18-8.24 Manifold distances and heights",
          "8.25 Reducers",
          "8.26 Stern manifold",
          "8.27 Heating coils/heat exchangers",
          "8.28 Cargo temperatures",
          "8.29 Inert Gas System",
          "8.30 IG supply",
          "8.31 Cargo pumps at full capacity",
          "8.32 Cargo Pump Data",
          "8.33 Emergency portable cargo pump",
          "8.34-8.38 Tank cleaning equipment",
          "8.39-8.42 Tank monitoring/cooling/drier systems",
          "8.43 Steam on deck",
          "9.1 Mooring ropes, wires, tails and shackles",
          "9.2 Winches and brake testing",
          "9.3 Mooring bollards and bitts",
          "9.4 Mooring fairleads/chocks",
          "9.5 Shackles port/starboard",
          "9.6 Emergency towing forward",
          "9.7 Emergency towing aft",
          "9.8-9.10 Stern chock/fairlead/bollard",
          "9.11 Derrick/Crane description",
          "9.12 Accommodation ladder direction",
          "9.13 Portable gangway",
          "9.14 OCIMF recommendations",
          "9.15-9.18 Chain stoppers/chock/fairlead",
          "10.1 Speed",
          "10.2 Main propulsion fuel",
          "10.3 Bunker Tank Capacities",
          "10.4 Propeller pitch",
          "10.5 Engines",
          "10.6 Bow thruster BHP",
          "10.7 Stern thruster BHP",
          "10.8 EEDI Rating",
          "10.9 EEXI Rating",
          "10.10 CII Rating",
          "10.11 EIV Rating",
          "10.12 NOx control level",
          "10.13 Exhaust Gas Cleaning System",
          "10.14 Scrubber type",
          "11.1 OCIMF/ICS STS compliance",
          "11.2 Crane/derrick outreach",
          "11.3 Last STS operation",
          "11.4 Ship specific STS plan",
          "12.1 Last three cargoes/charterers/voyages",
          "12.2 Pollution/grounding/collision/allision incident",
          "12.3 Last PSC inspection",
          "12.4 Outstanding PSC deficiencies",
          "12.5 Recent oil company inspections/screenings",
          "12.6 Last SIRE inspection",
          "12.6.1 Last CDI inspection",
          "12.7 Additional operational information"
        ].map((label, index) => ({ id: `q88_ops_${index}`, label, kind: "textarea" }))
      }
    ]
  },
  {
    id: "lng-q88",
    title: "INTERTANKO Standard Gas Form - LNG",
    menuLabel: "Q88 LNG",
    standard: ["INTERTANKO Q88", "SIGTTO", "OCIMF/ICS STS"],
    domain: "LNG vessel particulars",
    sections: [
      {
        id: "general",
        title: "1. General Information",
        source: "Gas_Form_C_-_LNG_r2.doc",
        fields: [
          "Vessel's name (IMO number)",
          "Flag/Port of Registry",
          "Date delivered/Builder",
          "Hull Type",
          "Call sign/MMSI",
          "Vessel's contact details (satcom/email)",
          "Classification society",
          "Class notation",
          "Previous Classification Society / Date of Change",
          "EEDI Rating",
          "CAP rating",
          "Registered owner - Full style",
          "Technical operator - Full style",
          "Commercial operator - Full style",
          "Qualified Individual - Full style",
          "P&I Club - Full Style",
          "Length overall (LOA)",
          "Extreme breadth (Beam)",
          "Moulded depth",
          "Distance bow to bridge",
          "Gross Tonnage",
          "Net Tonnage",
          "Suez Canal Tonnage - Gross/Net",
          "Loadline summer/winter/tropical/ballast",
          "FWA/TPC at summer draft",
          "Design Draft",
          "Max height of mast above waterline",
          "Multiple SDWT / Maximum deadweight"
        ].map((label, index) => ({ id: `lng_general_${index}`, label, kind: index > 10 ? "textarea" : "text" }))
      },
      {
        id: "cargo-machinery",
        title: "2-9. Cargo, machinery, manifold, propulsion, STS",
        source: "Gas_Form_C_-_LNG_r2.doc",
        fields: [
          "Type of Cargo Tank",
          "Independent tank type",
          "Cargo tank containment system",
          "Minimum design tank pressure",
          "Maximum design tank pressure",
          "Minimum design tank temperature",
          "Maximum design tank temperature",
          "Cargo piping material",
          "Design natural boil-off rate",
          "Maximum loading rate with vapour return",
          "Maximum loading rate without vapour return",
          "Cargo tank capacities",
          "Total capacity of all tanks 100%",
          "Number of mooring winches",
          "Mooring lines on drum",
          "Mooring lines material",
          "Ship design minimum breaking load",
          "Number and SWL of cranes",
          "Main cargo pumps",
          "Stripping/spray pumps",
          "Emergency cargo pumps",
          "ESD shore connection type",
          "Cargo Control Room",
          "Remote and local gauging system",
          "High duty compressors",
          "Low duty compressors",
          "Vaporizers",
          "Reliquefaction plant",
          "Inert gas system",
          "Nitrogen plant",
          "Manifold distances and heights",
          "Local pressure gauges outboard of manifold valves",
          "SIGTTO manifold compliance",
          "Manifold valve type",
          "ANSI Class 150 reducers",
          "Main and auxiliary engines",
          "Fuel for propulsion/generating plant",
          "Propeller and bow thruster",
          "Bunker tanks capacity",
          "Ballast tank total capacity",
          "OCIMF/ICS Ship To Ship Transfer Guide compliance"
        ].map((label, index) => ({ id: `lng_ops_${index}`, label, kind: "textarea" }))
      }
    ]
  },
  {
    id: "sire-viq",
    title: "SIRE VIQ 7 Vessel Inspection",
    menuLabel: "SIRE",
    standard: ["OCIMF SIRE", "SOLAS", "MARPOL", "ISPS Code"],
    domain: "Vessel inspection",
    sections: [
      {
        id: "chapters",
        title: "VIQ Chapters",
        source: "SIRE-Vessel.pdf",
        fields: [
          "1.1 Name of the vessel",
          "1.2 Vessel IMO Number",
          "1.3 Date the inspection was completed",
          "1.4 Full inspection completed",
          "1.5 Port of inspection",
          "1.6 Flag",
          "1.7 Deadweight",
          "1.8 Date vessel delivered",
          "1.9 OCIMF inspecting company",
          "1.10 Inspector boarded date/time",
          "1.11 Inspector departed date/time",
          "1.12 Time taken for inspection",
          "1.13 Name of inspector",
          "1.14 HVPQ maintained and available",
          "1.15 Vessel operation at inspection",
          "1.16 Product(s) handled",
          "1.17 Vessel type",
          "1.18 Hull type",
          "1.19 Vessel operator",
          "1.20 Current operator responsibility date",
          "1.21 Last PSC inspection date",
          "1.22 Last PSC inspection port",
          "1.23 Classification society",
          "1.24 Class Certificate expiry date",
          "1.25 Last class-credited drydock/repair/in-water survey",
          "1.26 Class Survey Status Report and records"
        ].map((label, index) => ({ id: `sire_ch1_${index}`, label, kind: "textarea" }))
      },
      {
        id: "sire-ch2-7",
        title: "Chapters 2-7 Certification, Crew, Navigation, Safety, Pollution, Security",
        source: "SIRE-Vessel.pdf",
        fields: [
          "2.1 Statutory certificates valid and endorsed",
          "2.2 P&I Club International Group membership",
          "2.3 ISM procedures manuals",
          "2.4 Operator representative biannual visits",
          "2.5 Operator internal audit and close-out",
          "2.6 Master SMS review and reporting",
          "2.7 Conditions of class / recommendations / memoranda",
          "2.8 Classification Society CAP",
          "2.9 Cargo/ballast/void/tank inspection procedures",
          "2.10 Oil Record Books correctly completed",
          "2.11 Disposal of oily water/sludge to cargo/slop tank",
          "2.12 VOC Management Plan",
          "2.13 Ballast Water and Sediments Management Plan",
          "2.14 SEEMP and officer awareness",
          "2.15 Structural damage evidence",
          "2.16 Sighted tanks/spaces condition",
          "3.1 Manning level vs Minimum Safe Manning",
          "3.2 Hours of work/rest compliance",
          "3.3 Common language communication",
          "3.4 Master ship handling course",
          "3.5 SIRE officer matrix",
          "3.6 Cargo transfer officer certificates",
          "3.7 ECDIS generic/specific training",
          "3.8 Drug and Alcohol measures",
          "4.1 Navigation procedures familiarity",
          "4.2 Navigational activity records",
          "4.3 Bridge equipment testing",
          "4.4 Fire and safety rounds",
          "4.5 UKC policy familiarity",
          "4.6 Bridge manning",
          "4.7 Navigation equipment good order",
          "4.8 Navigation lights",
          "4.9 Magnetic/Gyro compass",
          "4.10 Hand steering use",
          "4.11 VDR data retention",
          "4.12 Chart and publication management",
          "4.13 Navtex/Navarea warnings",
          "4.14 ECDIS operation",
          "4.15 ECDIS safety parameters",
          "4.16 Previous voyage charts",
          "4.17 ECDIS requirements",
          "4.18 Safe navigation compliance",
          "4.19 Echo sounder requirements",
          "4.20 Berth to berth passage plan",
          "4.21 Distress and urgency communications",
          "4.22 Ship security alert system",
          "4.23 GMDSS testing",
          "4.24 Radio maintenance programme",
          "4.25 EPIRB",
          "4.26 Intrinsically safe portable radios",
          "4.27 Survival craft VHF/SAR locating devices",
          "5.1 Risk assessment process",
          "5.2 Permit to work system",
          "5.3 Safety Officer",
          "5.4 Fixed and portable safety systems",
          "5.5 PPE requirements",
          "5.6 Intrinsically safe electronic equipment",
          "5.7 Safety meetings",
          "5.8 Incident reporting",
          "5.9 ISGOTT Ship/Shore Safety Check",
          "5.10 External doors/ports/windows closed",
          "5.11 Loose gear secured",
          "5.12 Fire and safety equipment familiarity",
          "5.13 Emergency duties and muster",
          "5.14 Lifeboat/fire drills",
          "5.15 Life-saving equipment training",
          "5.16 Enclosed space entry requirements",
          "5.17 Pump/compressor/trunk space entry",
          "5.18 Pump/compressor/trunk ventilation",
          "5.19 Fire/flooding dampers",
          "5.20 Incapacitated person lifting arrangements",
          "5.21 Adjacent spaces atmosphere monitoring",
          "5.22 Fixed atmosphere monitoring",
          "5.23 Portable gas detection",
          "5.24 Hot work",
          "5.25 Electric welding",
          "5.26 Gas welding and burning",
          "5.27 Life-saving appliance requirements",
          "5.28 Lifeboat/liferaft/rescue boat maintenance",
          "5.29 Lifeboats",
          "5.30 Rescue boat",
          "5.31 Lifebuoys and pyrotechnics",
          "5.32 Lifejackets",
          "5.33 Immersion suits",
          "5.34 Fire training manuals/booklets",
          "5.35 Fixed firefighting equipment",
          "5.36 Foam compound tests",
          "5.37 International Shore Connection",
          "5.38 Fire mains/pumps/hoses/nozzles/valves",
          "5.39 Fire detection/alarm testing",
          "5.40 Fixed fire extinguishing systems",
          "5.41 Emergency fire pump",
          "5.42 Portable fire extinguishers",
          "5.43 Breathing apparatus/fireman's outfits",
          "5.44 EEBD",
          "5.45 Fire flaps",
          "5.46 MSDS",
          "5.47 Safe access",
          "5.48 Cargo/bunker samples storage",
          "6.1 SOPEP duties",
          "6.2 Deck boundary coaming/scuppers",
          "6.3 Small oil/chemical spills",
          "6.4 Overboard/sea valves secured",
          "6.5 Ballast lines through cargo/bunker tanks",
          "6.6 Manifold spill containers",
          "6.7 Bunker pipeline annual test",
          "6.8 Manifold blanks/drains/vents",
          "6.9 Tank vent spill containment",
          "6.10 Oily water disposal arrangements",
          "6.11 Bilge high level alarms",
          "6.12 Pipeline draining and pump room bilge disposal",
          "6.13 ODME",
          "6.14 Oily water pumping/disposal",
          "6.15 Emergency bilge pumping",
          "6.16 Sludge/machinery waste disposal",
          "6.17 Oily water separator",
          "6.18 Warning notices",
          "6.19 OWS automatic stopping device / ORB entries",
          "6.20 Garbage segregation/storage",
          "6.21 BWTS operation",
          "6.22 BWTS maintenance",
          "7.1 Ship Security Plan",
          "7.2 CSO contact awareness",
          "7.3 Port call security records",
          "7.4 Security training/maintenance records",
          "7.5 SSO training",
          "7.6 Security communications equipment",
          "7.7 SSAS tests",
          "7.8 Passage plan security information",
          "7.9 Voyage/transit security risk assessment",
          "7.10 Vessel hardening",
          "7.11 Voluntary security reporting procedures",
          "7.12 Deck watch",
          "7.13 Security charts/publications/guidelines",
          "7.14 Cyber Security Policy",
          "7.15 Physical access control",
          "7.16 Personal devices policy",
          "7.17 Cyber Security awareness"
        ].map((label, index) => ({ id: `sire_ch2_7_${index}`, label, kind: "select", options: statusOptions }))
      },
      {
        id: "sire-ch8-12",
        title: "Chapters 8-12 Cargo, Mooring, Engine, Condition, Ice",
        source: "SIRE-Vessel.pdf",
        fields: [
          "8 Cargo and Ballast Systems - Petroleum",
          "8 Cargo and Ballast Systems - Chemicals",
          "8 Cargo and Ballast Systems - LPG",
          "8 Cargo and Ballast Systems - LNG",
          "9.1 Certificates for mooring lines and wires",
          "9.2 Mooring System Management Plan",
          "9.3 Line Management Plan",
          "9.4 Line inspection/retirement/wear zone policies",
          "9.5 Mooring lines/tails industry guidelines",
          "9.6 Bow stopper SWL certificate",
          "9.7 Winch brake testing",
          "9.8 Moorings deployed and tended",
          "9.9 Mooring lines secured correctly",
          "9.10 Powered mooring lines reeled/braked",
          "9.11 Split drum winches",
          "9.12 Mooring tails connections",
          "9.13 Mooring line stowage and area condition",
          "9.14 Mooring winches/foundations",
          "9.15 Brake linings/drums/pins",
          "9.16 Ex d mooring winch motors",
          "9.17 Mooring wires/lines/tails/connecting apparatus",
          "9.18 Fairleads and rollers",
          "9.19 Mooring equipment SWL marking",
          "9.20 Windlasses/anchors/cables",
          "9.21 Anchors cleared",
          "9.22 Bitter end arrangements",
          "9.23 Chain locker doors",
          "9.24 Anchor windlass design limitations",
          "9.25 SPM equipment OCIMF recommendations",
          "9.26 SPM equipment requirements",
          "9.27 Bow stopper safeguards",
          "9.28 Emergency towing arrangements",
          "9.29 Emergency Towing Procedures",
          "10.1 Machinery plant safe operation",
          "10.2 UMS operation",
          "10.3 Chief engineer standing orders",
          "10.4 Machinery space safe entry",
          "10.5 Engine room log book",
          "10.6 Essential emergency equipment",
          "10.7 Fuel/lube/hydraulic oil testing",
          "10.8 Bunkering safe transfer",
          "10.9 ECA requirements",
          "10.10 Emissions control precautions",
          "10.11 Exhaust Gas Cleaning System",
          "10.12 Planned Maintenance System",
          "10.13 Critical equipment list",
          "10.14 Engineer call alarm",
          "10.15 Machinery space illumination/escape routes",
          "10.16 Testing requirements",
          "10.17 Remote fuel shutoff",
          "10.18 Ventilation fan stops",
          "10.19 Fuel pipe screening/jacketing",
          "10.20 Purifier rooms cleanliness",
          "10.21 UMS bearing temperature monitoring",
          "10.22 Hydraulic aggregate fire protection",
          "10.23 Switchboard/electrical equipment",
          "10.24 Deck insulation",
          "10.25 Gauge glass closing devices",
          "10.26 Sounding devices",
          "10.27 Moving machinery guards",
          "10.28 Lifting devices records",
          "10.29 Machinery/steering spaces cleanliness",
          "10.30 Bilge high level alarm",
          "10.31 Seawater pumps/sea chests",
          "10.32 Applicable machinery items in good order",
          "10.33 Manoeuvring controls takeover",
          "10.34 Emergency generator",
          "10.35 Emergency generator fuel",
          "10.36 Emergency batteries",
          "10.37 Electrical equipment",
          "10.38 Switchboard earth faults",
          "10.39 Steering gear test",
          "10.40 Steering gear reserve tank",
          "10.41 Wheelhouse communications",
          "10.42 Steering gear access",
          "10.43 Steering compartment safety",
          "10.44 Watertight doors",
          "10.45-10.54 LNG bunkering controls",
          "11.1 Hull condition",
          "11.2 Hull markings",
          "11.3 Weather decks condition",
          "11.4 Service pipework condition",
          "11.5 Pipe stands/clamps/supports",
          "11.6 Deck openings/weather tightness",
          "11.7 Vents and air pipes",
          "11.8 Superstructure condition",
          "11.9 Deck lights",
          "11.10 Electrical equipment",
          "11.11 Ex d light fittings",
          "11.12 Forecastle/internal/storeroom condition",
          "11.13 Accommodation/public/sanitary/food spaces",
          "11.14 Laundries",
          "11.15 Ship hospital",
          "11.16 Accommodation electrical equipment",
          "11.17 Refrigerated spaces alarms",
          "12.1-12.21 Ice and sub-zero operations"
        ].map((label, index) => ({ id: `sire_ch8_12_${index}`, label, kind: "select", options: statusOptions }))
      }
    ]
  },
  {
    id: "port-operations-risk",
    title: "Port Operations and Container Terminal Risk",
    menuLabel: "Port Operations",
    standard: ["ISO 31010", "FMEA", "HIRA", "Port Operations"],
    domain: "Terminal operations, cargo handling, pilotage, tug services",
    sections: [
      {
        id: "operations",
        title: "Port Operations Questionnaire",
        source: "Port-Operations.pdf",
        fields: [
          "A.1 Country",
          "A.2 Name of Port/Terminal",
          "A.3 Category of Respondent",
          "B.1 Who is responsible for undertaking cargo handling",
          "B.2 Volume of cargo handled by type",
          "B.3 Equipment Operators - positions/skills and actual number available",
          "B.3 Port-workers - positions/skills and actual number available",
          "B.4 Skills acquisition for cargo handling",
          "B.5a Continuing skills enhancement program - Portworker",
          "B.5b Continuing skills enhancement program - Equipment Operator",
          "B.6 Compensation basis",
          "B.7 Monetary and non-monetary benefits",
          "B.8a Quay equipment - type, capacity, units",
          "B.8b Yard equipment - type, capacity, units",
          "B.9a Lead time for requesting portworker",
          "B.9b Lead time for requesting equipment",
          "B.10 Availability of portworkers/equipment upon request",
          "B.11 Assignment method for cargo handling operation",
          "B.12 Cargo Location System",
          "B.13a Documents used - containerized operations",
          "B.13b Documents used - non-containerized operations",
          "B.13c Unique data elements",
          "B.14 Documentation problems and corrective actions",
          "B.15 Security measures in operational area",
          "B.16a Security breaches - cargo",
          "B.16b Security breaches - port personnel/users",
          "B.17 Claims procedures for loss/damage",
          "B.18a Billing and collection system automation",
          "B.18b Billing system description and flowcharts",
          "B.19 Source documents received on time for billing",
          "B.20 Source document data complete and accurate",
          "B.21 Uniform cargo handling tariff schedule",
          "B.22 Authorized tariff billing and collection",
          "B.23 Interfaces with other port parties/systems",
          "B.24 Productivity standards",
          "B.25 Reasons for inability to meet target productivity",
          "B.26 Amenities/facilities at port/terminal/operational areas",
          "C.1 Pilotage service compulsory",
          "C.2a Pilotage service provider",
          "C.2b Pilot qualifications",
          "C.2c Vessels using pilotage services per year",
          "C.2d Number of harbor pilots available",
          "C.2e Pilotage rates authority",
          "C.3 Accreditation/licensing requirements for pilots",
          "C.4 Pilot accreditation procedures and flowchart",
          "D.1 Tug services compulsory",
          "D.2 Tug service provider",
          "D.3 Tugboat capacity and required number",
          "D.4 Tugboat operator liabilities and obligations",
          "D.5 Types of tug services",
          "D.6 Berthing/unberthing manpower and qualifications",
          "D.7 Tug service accreditation/licensing requirements",
          "D.8a Mandatory tug requirements for berthing",
          "D.8b Mandatory tug requirements for unberthing",
          "D.8c Tug service rate determination",
          "D.10 Liability determination mechanism"
        ].map((label, index) => ({ id: `port_ops_${index}`, label, kind: "textarea" }))
      },
      {
        id: "risk",
        title: "Container Terminal Risk Evaluation",
        source: "11.06_03.pdf",
        fields: [
          { id: "risk_category", label: "Risk category", kind: "select", options: ["Health / safety", "Environment", "Finance", "Community / Brand", "Security"] },
          { id: "risk_description", label: "Risk description", kind: "textarea" },
          { id: "severity_index", label: "Severity Index SI (1 very low - 5 very high)", kind: "rating" },
          { id: "probability_index", label: "Probability Index PI (1 rare - 5 almost certain)", kind: "rating" },
          { id: "criticality_index", label: "Criticality Index CI = SI x PI", kind: "number" },
          { id: "criticality_level", label: "Criticality level", kind: "select", options: ["Negligible risk <5", "Risk to follow [5,15]", "Risk to treat >15"] },
          { id: "controls", label: "Current controls", kind: "textarea" },
          { id: "mitigation", label: "Mitigation / corrective action", kind: "textarea" }
        ]
      }
    ]
  },
  {
    id: "terminal-safety-controls",
    title: "Terminal Safety Controls",
    menuLabel: "Safety",
    standard: ["D.Lgs. 81/2008", "D.Lgs. 272/1999", "ISO 45001", "Terminal operating procedures"],
    domain: "Safety of terminal operations, workplace safety, fire and emergency readiness",
    sections: [
      {
        id: "safety-signage",
        title: "Safety boards, signage and site rules",
        source: "Terminal ESG ISPS Digital Action Plan.xlsx - SAFETY",
        fields: complianceFields("safety_signage", [
          { label: "Safety boards and mandatory site rules are visible at gate, quay, yard and warehouses", normRef: "D.Lgs. 81/2008", guidance: "Verify actual visibility, language, update date and consistency with local traffic and operational rules." },
          { label: "Emergency evacuation maps, muster points and alarm instructions are posted and readable", normRef: "D.Lgs. 81/2008" },
          { label: "Speed limits, pedestrian routes, restricted areas and vehicle segregation signs are clear", normRef: "UNI EN ISO 7010" },
          { label: "Warning signs for cranes, lifting areas, moving equipment, electrical rooms and hazardous zones are present", normRef: "UNI EN ISO 7010" },
          { label: "Visitor and contractor safety induction is delivered before terminal access", normRef: "D.Lgs. 81/2008" },
          { label: "Stop-work authority and unsafe-condition reporting channels are communicated to workers", normRef: "ISO 45001:2018" },
          { label: "Safety signage is periodically inspected and damaged signs are replaced", normRef: "D.Lgs. 81/2008" }
        ])
      },
      {
        id: "safety-ppe",
        title: "PPE, access and operational behavior",
        source: "D.Lgs. 81/2008 / D.Lgs. 272/1999 / terminal procedures",
        fields: complianceFields("safety_ppe", [
          { label: "PPE matrix covers employees, contractors, visitors and high-risk tasks", normRef: "D.Lgs. 81/2008" },
          { label: "PPE is available, suitable, inspected and actually worn in operational areas", normRef: "D.Lgs. 81/2008" },
          { label: "Vehicle/pedestrian segregation is respected in yard, gate, quay and warehouse areas", normRef: "D.Lgs. 272/1999" },
          { label: "Housekeeping, lighting, barriers and guards around dangerous machinery are adequate", normRef: "D.Lgs. 81/2008" },
          { label: "Cargo handling equipment operators hold valid qualification and authorization", normRef: "D.Lgs. 81/2008" },
          { label: "Pre-use checks for forklifts, reach stackers, cranes and lifting accessories are retained", normRef: "D.Lgs. 272/1999" },
          { label: "Unsafe acts observed during site visit are recorded and escalated", normRef: "ISO 45001:2018" }
        ])
      },
      {
        id: "safety-documents",
        title: "Safety documents, permits and contractor control",
        source: "DVR / DUVRI / work permits / terminal safety procedures",
        fields: complianceFields("safety_docs", [
          { label: "DVR, DUVRI, POS/PSC or equivalent safety documents are available, current and approved", normRef: "D.Lgs. 81/2008" },
          { label: "Safety roles, RSPP/ASPP, supervisors and emergency teams are appointed and communicated", normRef: "D.Lgs. 81/2008" },
          { label: "Work permits are controlled for hot work, confined spaces, lifting and electrical works", normRef: "D.Lgs. 81/2008" },
          { label: "Contractor safety qualification is checked before procurement and terminal access", normRef: "D.Lgs. 81/2008" },
          { label: "Method statements and risk assessments are reviewed before non-routine work", normRef: "ISO 45001:2018" },
          { label: "Incident, near miss and unsafe condition reporting is active and traceable", normRef: "ISO 45001:2018" },
          { label: "Corrective actions from incidents and inspections have owner, due date and closure evidence", normRef: "ISO 45001:2018" },
          { label: "Safety audit records and legal compliance evaluations are retained", normRef: "ISO 45001:2018" },
          { label: "Terminal operating procedures cover berth, yard, gate, warehouse and traffic interfaces", normRef: "D.Lgs. 272/1999" }
        ])
      },
      {
        id: "safety-fire-emergency",
        title: "Fire safety and emergency response",
        source: "D.Lgs. 81/2008 / emergency plan / fire prevention rules",
        fields: complianceFields("safety_fire", [
          { label: "Fire extinguishers, hydrants, pumps, hoses and fire protection devices are present and accessible", normRef: "D.M. 1/09/2021" },
          { label: "Fire equipment maintenance tags and registers are current", normRef: "D.M. 1/09/2021" },
          { label: "Emergency exits, escape routes, alarm points and muster points are clear and signed", normRef: "D.M. 2/09/2021" },
          { label: "Internal fire team, external firefighters and port authority interface are defined and tested", normRef: "D.M. 2/09/2021" },
          { label: "Emergency contacts, call tree and control-room escalation are current and known", normRef: "D.M. 2/09/2021" },
          { label: "Fire drills, evacuation drills and spill response exercises are planned and recorded", normRef: "D.M. 2/09/2021" },
          { label: "Flammable materials storage, charging areas and hot-work controls are managed", normRef: "D.M. 3/09/2021" },
          { label: "Emergency access for rescue vehicles is clear and tested", normRef: "D.Lgs. 272/1999" },
          { label: "Severe weather, rescue and terminal shutdown scenarios are included in emergency planning", normRef: "ISO 45001:2018" },
          { label: "Lessons learned from drills and emergencies are converted into corrective actions", normRef: "ISO 45001:2018" }
        ])
      },
      {
        id: "safety-training",
        title: "Training, competence and supervision",
        source: "D.Lgs. 81/2008 / ISO 45001 competence controls",
        fields: complianceFields("safety_training", [
          { label: "Training matrix covers port workers, equipment operators, supervisors and emergency teams", normRef: "D.Lgs. 81/2008" },
          { label: "Role-specific licenses and refresher training are current for safety-critical tasks", normRef: "D.Lgs. 81/2008" },
          { label: "Toolbox talks and pre-shift briefings address current operations and local risks", normRef: "ISO 45001:2018" },
          { label: "Supervisors verify compliance with PPE, traffic, lifting and housekeeping rules", normRef: "D.Lgs. 272/1999" },
          { label: "Temporary workers and contractors receive understandable instructions", normRef: "D.Lgs. 81/2008" },
          { label: "Competence gaps are recorded and followed up", normRef: "ISO 45001:2018" },
          { label: "Workers are consulted on hazards, incidents and improvement actions", normRef: "ISO 45001:2018" }
        ])
      },
      {
        id: "safety-firstaid-interface",
        title: "First aid interface inside safety operations",
        source: "D.Lgs. 81/2008 / DM 388/2003",
        fields: complianceFields("safety_firstaid", [
          { label: "First aiders are available by shift and operational area", normRef: "D.M. 388/2003" },
          { label: "First aid kits and emergency showers/eyewash are inspected", normRef: "D.M. 388/2003" },
          { label: "Accident response includes communication, rescue route and external medical support", normRef: "D.Lgs. 81/2008" },
          { label: "High-risk areas have adequate emergency medical response arrangements", normRef: "D.M. 388/2003" },
          { label: "Medical emergency drills or tabletop exercises are recorded", normRef: "D.Lgs. 81/2008" },
          { label: "Accident medical response reports and corrective actions are traceable", normRef: "ISO 45001:2018" }
        ])
      }
    ]
  },
  {
    id: "terminal-security-isps",
    title: "Terminal Security and ISPS Controls",
    menuLabel: "Security/ISPS",
    standard: ["SOLAS XI-2", "ISPS Code Part A/B", "Reg. CE 725/2004", "D.M. 18/06/2004", "D.Lgs. 203/2007", "MIT Circ. 8/2023"],
    domain: "Port facility security, PFSA, PFSP, PFSO, access control, surveillance and drills",
    sections: [
      {
        id: "isps-pfsa-pfsp",
        title: "PFSA and threat assessment",
        source: "ISPS Code A/15-16-17; Reg. CE 725/2004; MIT Circ. 8/2023",
        fields: complianceFields("isps_pfsa", [
          { label: "PFSA exists, is approved, current and reviewed after threat or terminal changes", normRef: "ISPS Code", guidance: "Check approval evidence, review cycle and triggers after layout, traffic, threat or operation changes." },
          { label: "PFSA identifies critical assets, infrastructure and ship/port interface dependencies", normRef: "ISPS Code" },
          { label: "PFSA identifies threats, vulnerabilities and likely attack/sabotage scenarios", normRef: "ISPS Code" },
          { label: "PFSA evaluates existing countermeasures and residual vulnerabilities", normRef: "ISPS Code" },
          { label: "PFSA considers personnel, contractor, waterside, cargo and cyber/security-technology weaknesses", normRef: "ISPS Code" },
          { label: "PFSA outputs are traceably reflected in PFSP procedures and controls", normRef: "Reg. (CE) 725/2004" }
        ])
      },
      {
        id: "isps-pfsp",
        title: "PFSP, security levels and protected documents",
        source: "ISPS Code A/16; Reg. CE 725/2004; MIT Circ. 8/2023",
        fields: complianceFields("isps_pfsp", [
          { label: "PFSP exists, is approved, current and protected from unauthorized disclosure", normRef: "ISPS Code" },
          { label: "PFSP covers access control, restricted areas, cargo, stores, baggage and ship/port interface", normRef: "ISPS Code" },
          { label: "Security Levels 1, 2 and 3 procedures are defined, communicated and actionable", normRef: "ISPS Code" },
          { label: "PFSP includes communication with ships, port authority, police, customs and emergency services", normRef: "D.Lgs. 203/2007" },
          { label: "Security declarations, DoS handling and ship interface controls are defined where applicable", normRef: "ISPS Code" },
          { label: "PFSP amendments, distribution and version control are traceable", normRef: "MIT Circ. 8/2023" },
          { label: "Security-sensitive records are access-controlled and retained", normRef: "Reg. (CE) 725/2004" },
          { label: "Past ISPS findings and corrective actions are closed and effectiveness-checked", normRef: "Reg. (CE) 324/2008" },
          { label: "Local port security requirements are reflected in terminal procedures", normRef: "D.M. 18/06/2004" }
        ])
      },
      {
        id: "isps-pfso",
        title: "PFSO, duties, training and exercises",
        source: "ISPS Code A/17; MIT Circ. 8/2023",
        fields: complianceFields("isps_pfso", [
          { label: "PFSO is formally appointed, trained, available and known to key personnel", normRef: "ISPS Code" },
          { label: "PFSO duties are aligned with PFSA, PFSP, drills, audits and corrective actions", normRef: "ISPS Code" },
          { label: "Personnel with security duties receive training and refreshers appropriate to their role", normRef: "ISPS Code" },
          { label: "Security drills, exercises and lessons learned are planned, performed and recorded", normRef: "ISPS Code" },
          { label: "Incident reporting, escalation, authority notification and evidence preservation are defined", normRef: "MIT Circ. 8/2023" },
          { label: "Internal security audits and management reviews are retained", normRef: "Reg. (CE) 324/2008" }
        ])
      },
      {
        id: "isps-access-perimeter",
        title: "Access control, perimeter, CCTV and surveillance",
        source: "ISPS Code B/15; MIT Circ. 8/2023; PFSP",
        fields: complianceFields("isps_access", [
          { label: "Gate process verifies persons, vehicles, visitors, contractors, badges and authorization", normRef: "ISPS Code" },
          { label: "Restricted areas are identified, signed, physically protected and monitored", normRef: "ISPS Code" },
          { label: "Perimeter fences, barriers, anti-intrusion devices, waterside boundaries and lighting are effective", normRef: "ISPS Code" },
          { label: "CCTV coverage, camera uptime, blind spots, night visibility and monitoring are verified", normRef: "ISPS Code" },
          { label: "CCTV retention, access rights, evidence extraction and maintenance logs are controlled", normRef: "GDPR (EU) 2016/679" },
          { label: "Cargo, stores and critical infrastructure are protected against tampering and sabotage", normRef: "ISPS Code" },
          { label: "Temporary badges, lost badges and access revocation are controlled", normRef: "ISPS Code" },
          { label: "Waterside access and ship/shore interface are monitored during operations", normRef: "SOLAS XI-2" }
        ])
      },
      {
        id: "isps-doc-records",
        title: "ISPS records, declarations and authority interface",
        source: "ISPS Code / Reg. CE 725/2004 / MIT guidance",
        fields: complianceFields("isps_records", [
          { label: "Security incident log, drill records and audit records are retained and retrievable", normRef: "ISPS Code" },
          { label: "DoS and ship/port security communication records are retained where applicable", normRef: "ISPS Code" },
          { label: "Authority inspections, observations and corrective action evidence are traceable", normRef: "Reg. (CE) 324/2008" },
          { label: "PFSP confidential annexes are controlled and shared only with authorized persons", normRef: "Reg. (CE) 725/2004" },
          { label: "Security equipment faults and temporary compensating measures are recorded", normRef: "MIT Circ. 8/2023" },
          { label: "Threat level changes and instructions from authorities are logged and implemented", normRef: "D.Lgs. 203/2007" }
        ])
      }
    ]
  },
  {
    id: "terminal-environmental-controls",
    title: "Terminal Environmental Controls",
    menuLabel: "Environmental",
    standard: ["MARPOL Annex I/V/VI", "EU 2019/883", "RENTRI", "ISO 14001", "Terminal environmental procedures"],
    domain: "Environmental compliance, waste reception, MARPOL, emissions, spills and traceability",
    sections: [
      {
        id: "environment-waste",
        title: "Waste, MARPOL and port reception facilities",
        source: "EU 2019/883; MARPOL Annex V; Paris MoU 145xx analogy",
        fields: complianceFields("env_waste", [
          { label: "Ship-generated waste and port waste reception process is defined and implemented", normRef: "EU 2019/883" },
          { label: "Waste reception requests, notifications, receipts and handover records are complete", normRef: "EU 2019/883" },
          { label: "Waste segregation areas are identified, labelled and protected from weather/spill risks", normRef: "D.Lgs. 152/2006" },
          { label: "FIR/RENTRI or equivalent waste traceability records are complete", normRef: "D.Lgs. 116/2020" },
          { label: "MARPOL Annex V garbage controls and handling practices are verified", normRef: "MARPOL Annex V" },
          { label: "Contractors for waste transport/treatment are qualified and authorized", normRef: "D.Lgs. 152/2006" },
          { label: "Waste storage time, compatibility and housekeeping are controlled", normRef: "D.Lgs. 152/2006" }
        ])
      },
      {
        id: "environment-emissions-spill",
        title: "Emissions, spill prevention and emergency environmental response",
        source: "MARPOL Annex I/VI; ISO 14001; terminal procedures",
        fields: complianceFields("env_spill", [
          { label: "MARPOL Annex I oil pollution prevention controls are defined where relevant", normRef: "MARPOL Annex I" },
          { label: "Spill kits, drain protection and containment devices are available in risk areas", normRef: "D.Lgs. 152/2006" },
          { label: "Pollution prevention procedures cover bunkering, oily water, cargo residues and hazardous substances", normRef: "MARPOL Annex I" },
          { label: "Stormwater drains, separators and discharge points are inspected and maintained", normRef: "D.Lgs. 152/2006" },
          { label: "Environmental emergency drills and spill response training are performed and recorded", normRef: "ISO 14001:2015" },
          { label: "Environmental incidents, complaints, exceedances and corrective actions are tracked to closure", normRef: "ISO 14001:2015" },
          { label: "Ballast water/sediment interface risks are identified where relevant", normRef: "BWM Convention 2004" }
        ])
      },
      {
        id: "environment-air-energy",
        title: "Air emissions, energy and local environmental permits",
        source: "MARPOL Annex VI / ISO 14001 / local permits",
        fields: complianceFields("env_air", [
          { label: "MARPOL Annex VI air emission controls, fuel records or local permits are available where applicable", normRef: "MARPOL Annex VI" },
          { label: "Dust, noise, odour and emissions from cargo handling are assessed and controlled", normRef: "D.Lgs. 152/2006" },
          { label: "Energy use, equipment idling and emission-reduction actions are monitored", normRef: "ISO 14001:2015" },
          { label: "Environmental legal register includes permits, obligations and monitoring deadlines", normRef: "ISO 14001:2015" },
          { label: "Environmental objectives and improvement actions are defined and reviewed", normRef: "ISO 14001:2015" }
        ])
      },
      {
        id: "environment-soil-water",
        title: "Soil, water, chemicals and emergency receptors",
        source: "D.Lgs. 152/2006 / terminal environmental procedures",
        fields: complianceFields("env_soil", [
          { label: "Chemical and hazardous substance storage is labelled, segregated and bunded", normRef: "D.Lgs. 152/2006" },
          { label: "SDS/MSDS and emergency instructions are available for hazardous substances", normRef: "D.Lgs. 81/2008" },
          { label: "Drains, water receptors and sensitive areas are mapped for spill response", normRef: "D.Lgs. 152/2006" },
          { label: "Soil and water contamination controls are inspected after incidents or leaks", normRef: "D.Lgs. 152/2006" },
          { label: "Environmental contractors and laboratories are qualified where used", normRef: "ISO 14001:2015" }
        ])
      }
    ]
  },
  {
    id: "terminal-medical-readiness",
    title: "Terminal Medical and First Aid Readiness",
    menuLabel: "Medical",
    standard: ["D.Lgs. 81/2008", "DM 388/2003", "Terminal emergency procedures"],
    domain: "First aid, medical emergency response, rescue and occupational health readiness",
    sections: [
      {
        id: "medical-first-aid",
        title: "First aid organization and equipment",
        source: "D.Lgs. 81/2008; DM 388/2003",
        fields: complianceFields("medical_aid", [
          { label: "First aiders are appointed by shift, area and operational coverage", normRef: "D.M. 388/2003" },
          { label: "First aid boxes, stretchers, eyewash, showers and emergency medical equipment are present", normRef: "D.M. 388/2003" },
          { label: "First aid equipment inspection records are current", normRef: "D.M. 388/2003" },
          { label: "DAE/AED availability, maintenance, signage and trained users are verified where applicable", normRef: "D.M. 388/2003" },
          { label: "Emergency numbers, medical contacts and escalation are visible and known", normRef: "D.Lgs. 81/2008" },
          { label: "Medical emergency access routes for ambulance/rescue are clear and tested", normRef: "D.Lgs. 81/2008" }
        ])
      },
      {
        id: "medical-health-response",
        title: "Health surveillance and medical emergency response",
        source: "D.Lgs. 81/2008; terminal medical procedures",
        fields: complianceFields("medical_response", [
          { label: "Medico competente, health surveillance and fitness-for-duty records are managed for relevant roles", normRef: "D.Lgs. 81/2008" },
          { label: "Heat stress, fatigue, chemical exposure and manual handling risks have medical controls where applicable", normRef: "D.Lgs. 81/2008" },
          { label: "Medical emergency drills or tabletop exercises are recorded", normRef: "D.Lgs. 81/2008" },
          { label: "Accident medical response reports and corrective actions are traceable", normRef: "ISO 45001:2018" },
          { label: "Remote or isolated work areas have first aid communication and rescue arrangements", normRef: "D.Lgs. 81/2008" },
          { label: "Maritime medical response interface is considered for dangerous-goods ship/shore emergencies", normRef: "MFAG" }
        ])
      },
      {
        id: "medical-occupational",
        title: "Occupational health, welfare and medical records",
        source: "MLC 2006 / D.Lgs. 81/2008 occupational health controls",
        fields: complianceFields("medical_occupational", [
          { label: "Occupational health protocol covers role-specific hazards and periodic checks", normRef: "D.Lgs. 81/2008" },
          { label: "Medical confidentiality and access to health records are controlled", normRef: "GDPR (EU) 2016/679" },
          { label: "Medical care interface with port health authority or local emergency services is defined", normRef: "MLC 2006" },
          { label: "Return-to-work and restricted-duty controls are defined after injury/illness", normRef: "D.Lgs. 81/2008" },
          { label: "First aid and medical lessons learned are included in management review", normRef: "ISO 45001:2018" }
        ])
      }
    ]
  },
  {
    id: "terminal-people-fatigue",
    title: "Terminal People, Competence and Fatigue Management",
    menuLabel: "People/Fatigue",
    standard: ["D.Lgs. 81/2008", "D.Lgs. 272/1999", "ISO 45001", "Paris MoU rest-hours analogy"],
    domain: "People management, shift planning, competence, training, rest hours and contractor workforce control",
    sections: [
      {
        id: "people-shifts-fatigue",
        title: "Shift planning, rest hours and fatigue controls",
        source: "D.Lgs. 81/2008; Paris MoU 01307/01308/18201 analogy",
        fields: complianceFields("people_fatigue", [
          { label: "Shift patterns, overtime, night work and rest breaks are planned and controlled", normRef: "D.Lgs. 81/2008" },
          { label: "Fatigue risks for safety-critical roles are assessed and mitigated", normRef: "Paris MoU 18201" },
          { label: "Work/rest records are available and reviewed for employees and contractors", normRef: "Paris MoU 18201" },
          { label: "Fitness for duty, alcohol/drug or impairment controls are defined where applicable", normRef: "MLC 2006" },
          { label: "Supervisors can stop work or reassign staff when fatigue or fitness concerns arise", normRef: "D.Lgs. 81/2008" },
          { label: "Rest-hour deviations and repeated overtime are reviewed and corrected", normRef: "Paris MoU 18201" },
          { label: "Night work and lone work arrangements include escalation and welfare controls", normRef: "D.Lgs. 81/2008" }
        ])
      },
      {
        id: "people-competence-contractors",
        title: "Competence, training and contractor people management",
        source: "D.Lgs. 81/2008; ISPS training; ISO procurement",
        fields: complianceFields("people_competence", [
          { label: "Training matrix covers port workers, operators, security duties, emergency team and supervisors", normRef: "D.Lgs. 81/2008" },
          { label: "Role-specific licenses, qualifications and refreshers are current", normRef: "STCW" },
          { label: "Contractor induction, PPE, security awareness and emergency instructions are completed before access", normRef: "D.Lgs. 81/2008" },
          { label: "Language, communication and toolbox talks are adequate for mixed workforces", normRef: "ISO 45001:2018" },
          { label: "Performance, incidents and repeated findings are reviewed by personnel/contractor category", normRef: "ISO 9001:2015" }
        ])
      },
      {
        id: "people-labour",
        title: "Labour, welfare and human rights controls",
        source: "MLC 2006 / ESG labour controls",
        fields: complianceFields("people_labour", [
          { label: "Employment, contractor and temporary labour arrangements are legally controlled", normRef: "MLC 2006" },
          { label: "Working time, payroll and welfare complaints can be traced and escalated", normRef: "MLC 2006" },
          { label: "Discrimination, harassment and grievance mechanisms are communicated", normRef: "ISO 9001:2015" },
          { label: "Sanitary, drinking water, rest and welfare facilities are adequate", normRef: "D.Lgs. 81/2008" },
          { label: "Subcontractor people-management obligations are included in procurement controls", normRef: "ISO 9001:2015" }
        ])
      },
      {
        id: "people-communication",
        title: "Communication, supervision and learning",
        source: "ISO 45001 / ISO 9001 people and competence controls",
        fields: complianceFields("people_comm", [
          { label: "Operational briefings include safety, security, environmental and fatigue alerts", normRef: "ISO 45001:2018" },
          { label: "Supervisors have authority and competence to intervene on unsafe operations", normRef: "D.Lgs. 81/2008" },
          { label: "Lessons learned from findings are communicated to relevant crews and departments", normRef: "ISO 9001:2015" },
          { label: "Competence records are updated after training, incident or change of role", normRef: "ISO 45001:2018" },
          { label: "People KPIs include fatigue, training, incidents and contractor performance", normRef: "ISO 45001:2018" }
        ])
      }
    ]
  },
  {
    id: "terminal-it-cyber-cctv",
    title: "Terminal IT, Cybersecurity and CCTV Controls",
    menuLabel: "IT & Cyber/CCTV",
    standard: ["ISPS Code B/15", "NIS2 cyber governance", "Terminal IT/security procedures"],
    domain: "CCTV, access control systems, IT/cybersecurity, networks and operational resilience",
    sections: [
      {
        id: "cyber-cctv-access",
        title: "CCTV, access-control systems and security technology",
        source: "ISPS PFSA B/15 radio/telecommunication/computer systems",
        fields: complianceFields("cyber_cctv", [
          { label: "CCTV uptime, camera coverage, blind spots and night visibility are verified", normRef: "ISPS Code" },
          { label: "CCTV retention, extraction procedure and evidence chain are controlled", normRef: "GDPR (EU) 2016/679" },
          { label: "Access control software, badge systems, gate logs and visitor records are backed up and auditable", normRef: "ISPS Code" },
          { label: "Admin rights for CCTV/access systems are assigned, reviewed and revoked when no longer needed", normRef: "ISO 27001" },
          { label: "Security technology maintenance, faults and workaround procedures are recorded", normRef: "ISPS Code" },
          { label: "Privacy notices and lawful handling rules cover CCTV and visitor data", normRef: "GDPR (EU) 2016/679" },
          { label: "Security-sensitive footage and exports are shared only with authorized persons", normRef: "Reg. (CE) 725/2004" }
        ])
      },
      {
        id: "cyber-network-response",
        title: "Cyber architecture, remote access and incident response",
        source: "ISPS PFSA B/15; NIS2/cyber governance; terminal procedures",
        fields: complianceFields("cyber_network", [
          { label: "Network segmentation separates office IT, CCTV, access control, OT and terminal systems", normRef: "IEC 62443" },
          { label: "Remote vendor access is authorized, logged, time-limited and reviewed", normRef: "ISO 27001" },
          { label: "Backups for critical security and operational systems are protected and tested", normRef: "IMO MSC.428(98)" },
          { label: "Cyber incident response covers CCTV/access/terminal operations disruption", normRef: "IMO MSC.428(98)" },
          { label: "Cyber risks are included in business continuity and emergency response exercises", normRef: "IMO MSC.428(98)" },
          { label: "Patch, vulnerability and antivirus/EDR status are reviewed for critical assets", normRef: "ISO 27001" },
          { label: "Accounts, MFA and password policies are enforced for privileged users", normRef: "ISO 27001" }
        ])
      },
      {
        id: "cyber-ot",
        title: "OT, terminal systems and resilience",
        source: "IEC 62443 / IACS cyber resilience benchmarks",
        fields: complianceFields("cyber_ot", [
          { label: "Terminal operating systems and OT assets are inventoried with owner and criticality", normRef: "IEC 62443" },
          { label: "Interfaces between OT, gates, CCTV, access control and business systems are documented", normRef: "IACS UR E26" },
          { label: "Manual fallback procedures exist for gate/access/CCTV/terminal-system outage", normRef: "IMO MSC.428(98)" },
          { label: "Critical equipment configuration and recovery media are protected", normRef: "IACS UR E27" }
        ])
      },
      {
        id: "cyber-governance",
        title: "Cyber governance, privacy and auditability",
        source: "ISO 27001 / GDPR / maritime cyber guidance",
        fields: complianceFields("cyber_gov", [
          { label: "Cybersecurity roles, responsibilities and escalation contacts are defined", normRef: "ISO 27001" },
          { label: "Supplier and maintenance access to IT/OT/security systems is contractually controlled", normRef: "ISO 27001" },
          { label: "Logs for critical systems are retained and reviewed", normRef: "ISO 27001" },
          { label: "Personal data processing for CCTV, visitors and badges has retention and access rules", normRef: "GDPR (EU) 2016/679" },
          { label: "Cyber findings have owner, due date and effectiveness verification", normRef: "IMO MSC.428(98)" }
        ])
      }
    ]
  },
  {
    id: "terminal-sira-risk",
    title: "SIRA / Terminal Risk Assessment",
    menuLabel: "SIRA",
    standard: ["ISO 31010", "ISO 19011 risk-based auditing", "ISPS PFSA", "Terminal risk matrix"],
    domain: "Cross-domain safety, security, environmental, people and cyber risk assessment",
    sections: [
      {
        id: "sira-risk-register",
        title: "Integrated terminal risk register",
        source: "Terminal ESG ISPS Digital Action Plan.xlsx - SIRA",
        fields: [
          { id: "risk_category", label: "Risk category", kind: "select", options: ["Safety", "Security / ISPS", "Environmental", "Quality / ISO", "Medical", "People / Fatigue", "IT & Cyber", "Finance", "Community / Brand"], required: true },
          { id: "risk_description", label: "Risk description / scenario", kind: "textarea", required: true },
          { id: "asset_or_process", label: "Asset, area or process affected", kind: "textarea" },
          { id: "threat_or_hazard", label: "Threat / hazard / failure mode", kind: "textarea" },
          { id: "current_controls", label: "Current controls", kind: "textarea" },
          { id: "severity_index", label: "Severity Index SI (1 very low - 5 very high)", kind: "rating" },
          { id: "probability_index", label: "Probability Index PI (1 rare - 5 almost certain)", kind: "rating" },
          { id: "criticality_index", label: "Criticality Index CI = SI x PI", kind: "number" },
          { id: "criticality_level", label: "Criticality level", kind: "select", options: ["Negligible risk <5", "Risk to follow [5,15]", "Risk to treat >15"] },
          { id: "mitigation", label: "Mitigation / corrective action", kind: "textarea" },
          { id: "owner", label: "Owner", kind: "text" },
          { id: "due_date", label: "Due date", kind: "date" },
          { id: "effectiveness_check", label: "Follow-up effectiveness check", kind: "textarea" }
        ]
      },
      {
        id: "sira-domain-prompts",
        title: "Domain risk prompts",
        source: "ISPS PFSA / D.Lgs. 81 / MARPOL / cyber governance",
        fields: controlFields("sira_prompt", [
          "Security risk: assets, threats, vulnerabilities, countermeasures and residual risk are assessed",
          "Safety risk: task hazards, controls and emergency consequences are assessed",
          "Environmental risk: pollution scenarios, receptors, legal controls and response are assessed",
          "People/fatigue risk: shift load, rest, competence and supervision are assessed",
          "Cyber risk: CCTV/access/OT disruption and unauthorized access scenarios are assessed"
        ])
      }
    ]
  },
  {
    id: "ecovadis-isps-esg",
    title: "ESG, ISPS and Sustainable Procurement",
    menuLabel: "ESG/ISPS",
    standard: ["EcoVadis", "ISPS Code / SOLAS XI-2", "MARPOL Annex V/VI", "EU 2019/883", "D.Lgs. 81/2008", "D.Lgs. 272/1999"],
    domain: "ESG, security, environment, labour and procurement",
    sections: [
      {
        id: "esg",
        title: "Integrated ESG and Security Controls",
        source: "____chelist.docx requirements",
        fields: complianceFields("esg", [
          { label: "Environment", normRef: "ISO 14001:2015" },
          { label: "Labour & Human Rights", normRef: "MLC 2006" },
          { label: "Ethics", normRef: "ISO 9001:2015" },
          { label: "Sustainable Procurement", normRef: "ISO 9001:2015" },
          { label: "Port Facility Security Assessment", normRef: "ISPS Code" },
          { label: "Port Facility Security Plan", normRef: "Reg. (CE) 725/2004" },
          { label: "Security levels", normRef: "ISPS Code" },
          { label: "PFSO and threat control measures", normRef: "ISPS Code" },
          { label: "Ship-generated waste and port waste reception", normRef: "EU 2019/883" },
          { label: "MARPOL Annex V garbage controls", normRef: "MARPOL Annex V" },
          { label: "MARPOL Annex VI air emissions controls", normRef: "MARPOL Annex VI" },
          { label: "D.Lgs. 81/2008 worker safety controls", normRef: "D.Lgs. 81/2008" },
          { label: "D.Lgs. 272/1999 port safety controls", normRef: "D.Lgs. 272/1999" },
          { label: "Contractor and supplier qualification", normRef: "ISO 9001:2015" }
        ])
      }
    ]
  }
];

export const roadmap = [
  { label: "Avvio audit e criteri", templateId: "iso-questionnaire", sectionId: "audit-identity" },
  { label: "Document review", templateId: "iso-questionnaire", sectionId: "chapter-7-10" },
  { label: "Planning risk-based", templateId: "iso-questionnaire", sectionId: "audit-plan" },
  { label: "Site visit evidenze", templateId: "port-operations-risk", sectionId: "operations" },
  { label: "Findings", templateId: "iso-questionnaire", sectionId: "audit-result" },
  { label: "CAPA owner", templateId: "iso-questionnaire", sectionId: "audit-result" },
  { label: "Report integrato", templateId: "iso-questionnaire", sectionId: "audit-identity" },
  { label: "Follow-up efficacia", templateId: "iso-questionnaire", sectionId: "audit-result" }
];

export const departments = ["Management", "RSPP / Work safety", "Agency", "Yachting Agency", "Projects", "Chartering", "Road Surveys", "Accounting / Secretary"];
